import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import NotificationAlertOptions from "@/layouts/Alerts";
import { GLOBAL_NOTIFICATION_CONTAINER_ID } from "@/layouts/GlobalNotifications";
import ReactBSAlert from 'react-bootstrap-sweetalert';

import UpdateTranslatePageView from './UpdateTranslatePageView';
import {
  translatedpages,
  updateTranslatedPage,
  transpagebyid
} from '@/services/translationservice/translationActions';

/** One page_content entry; the step-3 editor replaces single fields of it. */
type PageContentEntry = Record<string, unknown>;

/** The translated page as this screen reads it from the list. */
type TranslatedPage = {
  _id: string;
  page_configuration?: { path?: string };
  parentPage?: { site?: { domain?: string } };
};

type TranslateFormData = {
  global_title: string;
  meta_keywords: string;
  meta_description: string;
  intro_text: string;
  target_language: string;
  requires_review: boolean;
  publish_statement: boolean;
  page_content: PageContentEntry[];
};

const UpdateTranslatePageContainer = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translatePage, setTranslatePage] = useState<TranslatedPage | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [alert, setAlert] = useState<React.ReactNode>(null);
  const [formData, setFormData] = useState<TranslateFormData>({
    global_title: '',
    meta_keywords: '',
    meta_description: '',
    intro_text: '',
    target_language: '',
    requires_review: false,
    publish_statement: false,
    page_content: []
  });

  const translatedPages = useAppSelector((state) => state.translatedpages.translatedpages);
  const fullPage = useAppSelector((state) => state.translatedpages.page);

  useEffect(() => {
          
    
    dispatch(transpagebyid(id))
 }, []);

  // Find the specific translated page from Redux store
  useEffect(() => {
    if (translatedPages && translatedPages.length > 0) {
      const foundPage = translatedPages.find(page => page._id === id);
      if (foundPage) {
        setTranslatePage(foundPage);
        setFormData({
          global_title: foundPage.global_title || '',
          meta_keywords: foundPage.meta_keywords || '',
          meta_description: foundPage.meta_description || '',
          intro_text: foundPage.intro_text || '',
          target_language: foundPage.target_language || '',
          requires_review: foundPage.requires_review || false,
          publish_statement: foundPage.publish_statement || false,
          page_content: foundPage.page_content || []
        });
      }
    }
  }, [translatedPages, id]);

  // Load translated pages if not already loaded
  useEffect(() => {
          
    
    dispatch(transpagebyid(id))

    if (!translatedPages || translatedPages.length === 0) {
      setLoading(true);
      dispatch(translatedpages())
        .finally(() => setLoading(false));
    }
  }, [dispatch, translatedPages]);

  const hideAlert = useCallback(() => setAlert(null), []);

  const handleInputChange = <K extends keyof TranslateFormData>(field: K, value: TranslateFormData[K]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async (requiresReview = false) => {
    if (!translatePage) {
      NotificationAlertOptions("error", 'Page not found', { containerId: GLOBAL_NOTIFICATION_CONTAINER_ID });
      return;
    }

    try {
      setSaving(true);

      const updatedFormData = {
        ...formData,
        requires_review: requiresReview
      };

      await dispatch(updateTranslatedPage({
        id: translatePage._id,
        updates: updatedFormData
      }));

      // updateTranslatedPage resolves only on success and throws on failure (handled
      // by the catch below), so there was no in-band error to branch on — the old
      // `result.error` check + else were unreachable dead code (SCRUM-754).
      NotificationAlertOptions("success", 'Translated page updated successfully!', { containerId: GLOBAL_NOTIFICATION_CONTAINER_ID });
      // Refresh the translated pages list
      dispatch(translatedpages());
      // Redirect to translate pages list
      navigate('/admin/translate-pages');
    } catch (error) {
      console.error('Error updating translated page:', error);
      NotificationAlertOptions("error", 'Error updating translated page', { containerId: GLOBAL_NOTIFICATION_CONTAINER_ID });
    } finally {
      setSaving(false);
    }
  };

  const handleShowSaveConfirmation = () => {
    setAlert(
      // @ts-expect-error react-bootstrap-sweetalert prop-types
      <ReactBSAlert
        custom
        style={{ display: 'block', marginTop: '-100px' }}
        title="Save Translated Page"
        btnSize="md"
        confirmBtnText="Yes, Save Now"
        cancelBtnText="Review Later"
        confirmBtnBsStyle="success"
        cancelBtnBsStyle="warning"
        showCancel
        focusConfirmBtn
        onConfirm={() => {
          hideAlert();
          handleSave(false); // requires_review = false
        }}
        onCancel={() => {
          hideAlert();
          handleSave(true); // requires_review = true
        }}
      >
        Do you want to save and publish this translated page immediately, or save it for review later?
        <br />
        <br />
        <strong>Yes, Save Now:</strong> The page will be published immediately (requires_review = false)
        <br />
        <strong>Review Later:</strong> The page will be saved for review (requires_review = true)
      </ReactBSAlert>
    );
  };

  const handleCancel = () => {
    navigate('/admin/translate-pages');
  };

  const handlePreview = async () => {
    if (!fullPage) {
      NotificationAlertOptions("error", 'Page not found', { containerId: GLOBAL_NOTIFICATION_CONTAINER_ID });
      return;
    }

    try {
      // Combine current form data with translate page data
      const previewData = {
        ...fullPage,
        ...formData
      };

      // The backend keeps the draft for a few minutes under a token; the public
      // renderer reads it back through ?preview=<token> (SCRUM-1604).
      const response = await fetch("/api/translations/save-preview-data", {
        method: 'POST',
        // Send the httpOnly auth cookie: this endpoint now requires auth
        // (SCRUM-480). Without credentials the cross-origin cookie is dropped
        // and the request would 401. Mirrors the shared `api` axios client
        // (withCredentials: true).
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(previewData)
      });

      if (response.ok) {
        const { token } = await response.json();

        // Get site domain from parentPage
        const siteDomain = fullPage?.parentPage?.site?.domain || translatePage?.parentPage?.site?.domain;

        // Create preview URL using the site domain
        const baseUrl = siteDomain ? `https://${siteDomain}` : 'http://localhost:3000';
        // `translatePage!`: unguarded as before, so a missing page still throws
        // into the catch below ("Error saving preview data").
        const previewUrl = `${baseUrl}/translatepage/${translatePage!.page_configuration?.path || ''}?preview=${encodeURIComponent(token)}`;

        // Open preview in new tab
        window.open(previewUrl, '_blank');
        NotificationAlertOptions("success", 'Preview data saved and opened!', { containerId: GLOBAL_NOTIFICATION_CONTAINER_ID });
      } else {
        NotificationAlertOptions("error", 'Failed to save preview data', { containerId: GLOBAL_NOTIFICATION_CONTAINER_ID });
      }
    } catch (error) {
      console.error('Error saving preview data:', error);
      NotificationAlertOptions("error", 'Error saving preview data', { containerId: GLOBAL_NOTIFICATION_CONTAINER_ID });
    }
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePageContentChange = (index: number, field: string, value: unknown) => {
    const updatedContent = [...formData.page_content];
    if (updatedContent[index]) {
      updatedContent[index] = {
        ...updatedContent[index],
        [field]: value
      };
      setFormData(prev => ({
        ...prev,
        page_content: updatedContent
      }));
    }
  };

  return (
    <UpdateTranslatePageView
      loading={loading}
      saving={saving}
      translatePage={translatePage}
      fullPage={fullPage}

      
      formData={formData}
      currentStep={currentStep}
      onInputChange={handleInputChange}
      onPageContentChange={handlePageContentChange}
      onSave={handleSave}
      onCancel={handleCancel}
      onPreview={handlePreview}
      onNextStep={handleNextStep}
      onPrevStep={handlePrevStep}
      alert={alert}
      onShowSaveConfirmation={handleShowSaveConfirmation}
    />
  );
};

export default UpdateTranslatePageContainer;